import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiCalendar, FiEdit2, FiSave, FiLock, FiCheck, FiX, FiAlertCircle, FiPhone, FiMapPin, FiBriefcase } = FiIcons;

const UserProfile = () => {
  const { user, updateProfile } = useAuth();
  const { updateTeamMember, tasks, projects, teamMembers } = useData();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
    jobTitle: '',
    avatar: '',
    phone: '',
    location: '',
    bio: '',
    password: '',
    confirmPassword: ''
  });
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Get the most up-to-date user data from the live teamMembers list
  const liveUser = teamMembers.find(m => m.id === user?.id) || user;

  // Calculate Real Stats
  const completedTasksCount = tasks.filter(t => t.assignedTo === user?.id && t.status === 'completed').length;
  const activeProjectsCount = projects.filter(p => p.teamIds?.includes(user?.id) && p.status === 'active').length;
  const performanceScore = liveUser?.performance?.score || 0;

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        department: user.department || '',
        jobTitle: user.jobTitle || '',
        avatar: user.avatar || '',
        phone: user.phone || '',
        location: user.location || '',
        bio: user.bio || ''
      }));
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (formData.password && formData.password !== formData.confirmPassword) {
      setErrorMsg("Passwords don't match");
      return;
    }

    const updates = {
      name: formData.name,
      email: formData.email,
      department: formData.department,
      jobTitle: formData.jobTitle,
      avatar: formData.avatar,
      phone: formData.phone,
      location: formData.location,
      bio: formData.bio
    };

    // Update Auth Context (Session)
    updateProfile(updates);
    
    // Update Data Context (Team List)
    if (user.id) {
      updateTeamMember(user.id, updates);
    }

    setSuccessMsg('Profile updated successfully!');
    setIsEditing(false);
    // Clear password fields
    setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));

    setTimeout(() => setSuccessMsg(''), 3000);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">User Profile</h1>
        <p className="text-gray-600 mt-1">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-1"
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <div className="relative inline-block">
                <img
                  src={user?.avatar || 'https://via.placeholder.com/150'}
                  alt={user?.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-white shadow-sm"
                />
                <div className={`absolute bottom-4 right-0 w-4 h-4 rounded-full border-2 border-white ${user?.role === 'super_admin' ? 'bg-purple-500' : 'bg-green-500'}`}></div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">{user?.name}</h2>
              <p className="font-medium text-blue-600">{user?.jobTitle || user?.role?.replace('_', ' ')}</p>
              <p className="text-sm text-gray-500 mt-1">{user?.department}</p>
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center space-x-3 text-gray-600">
                <SafeIcon icon={FiMail} className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm truncate">{user?.email}</span>
              </div>
              {user?.phone && (
                <div className="flex items-center space-x-3 text-gray-600">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{user.phone}</span>
                </div>
              )}
              {user?.location && (
                <div className="flex items-center space-x-3 text-gray-600">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{user.location}</span>
                </div>
              )}
              <div className="flex items-center space-x-3 text-gray-600">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">Member since {new Date(user?.joinDate || Date.now()).getFullYear()}</span>
              </div>
            </div>

            <button
              onClick={() => setIsEditing(!isEditing)}
              className={`w-full mt-6 flex items-center justify-center space-x-2 py-2 px-4 rounded-lg transition-colors ${
                isEditing
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <SafeIcon icon={isEditing ? FiX : FiEdit2} className="w-4 h-4" />
              <span>{isEditing ? 'Cancel Editing' : 'Edit Profile'}</span>
            </button>
          </div>
        </motion.div>

        {/* Profile Details / Edit Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2"
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Information' : 'Profile Details'}
              </h3>
              {successMsg && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center space-x-2 text-green-600 text-sm font-medium bg-green-50 px-3 py-1 rounded-full"
                >
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  <span>{successMsg}</span>
                </motion.div>
              )}
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title / Designation
                    </label>
                    <input
                      type="text"
                      value={formData.jobTitle}
                      onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g. Senior Developer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department
                    </label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="City, Country"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Avatar URL
                  </label>
                  <input
                    type="url"
                    value={formData.avatar}
                    onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Paste a direct image link (e.g., from Unsplash or Imgur)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio / About
                  </label>
                  <textarea
                    rows={3}
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Tell us a little about yourself"
                  />
                </div>

                <div className="pt-6 border-t border-gray-100">
                  <h4 className="flex items-center text-sm font-semibold text-gray-900 mb-4">
                    <SafeIcon icon={FiLock} className="w-4 h-4 mr-2" /> Change Password
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Leave blank to keep current"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                  {errorMsg && (
                    <p className="text-red-600 text-sm mt-2 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1" /> {errorMsg}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>Save Changes</span>
                  </motion.button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Full Name
                    </label>
                    <p className="text-gray-900 font-medium">{user?.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Email Address
                    </label>
                    <p className="text-gray-900 font-medium">{user?.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Job Title
                    </label>
                    <p className="text-gray-900 font-medium">{user?.jobTitle || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Department
                    </label>
                    <p className="text-gray-900 font-medium">{user?.department}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Phone Number
                    </label>
                    <p className="text-gray-900 font-medium">{user?.phone || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Location
                    </label>
                    <p className="text-gray-900 font-medium">{user?.location || 'Not set'}</p>
                  </div>
                </div>

                {user?.bio && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Bio
                    </label>
                    <p className="text-gray-900 font-medium leading-relaxed">{user.bio}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Activity Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-blue-50 rounded-xl">
            <div className="text-3xl font-bold text-blue-600 mb-2">{completedTasksCount}</div>
            <div className="text-sm text-gray-600 font-medium">Tasks Completed</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-xl">
            <div className="text-3xl font-bold text-green-600 mb-2">{activeProjectsCount}</div>
            <div className="text-sm text-gray-600 font-medium">Projects Active</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-xl">
            <div className="text-3xl font-bold text-purple-600 mb-2">{performanceScore}%</div>
            <div className="text-sm text-gray-600 font-medium">Performance Score</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UserProfile;